import React, { useState, useEffect, useCallback } from "react";
import { useInterval } from "usehooks-ts";

import { Button, type ButtonProps } from "./Button";

export type CountdownButtonProps = {
  startCountdown: boolean;
  /**
   * duration of the countdown in seconds
   */
  duration: number;
  initialLabel: React.ReactNode;
  countingLabel: (remainingTime: string) => React.ReactNode;
  /**
   * set this if the button needs to be clickable more than once.
   */
  onExpire?: () => void;
  finishedLabel: React.ReactNode;
  finishedVariant?: ButtonProps["variant"];
} & Omit<ButtonProps, "children">;

const MS_SECOND = 1000;

const CountdownButton = ({
  startCountdown,
  duration,
  initialLabel,
  countingLabel,
  finishedLabel,
  finishedVariant,
  onExpire,
  ...props
}: CountdownButtonProps) => {
  const [remaining, setRemaining] = useState(duration);
  const [isCounting, setIsCounting] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  // format seconds into M:SS
  const formatTime = useCallback((totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const paddedSeconds = seconds < 10 ? `0${seconds}` : seconds;
    return `${minutes}:${paddedSeconds}`;
  }, []);

  useEffect(() => {
    if (startCountdown) {
      setIsCounting(true);
      setIsExpired(false);
      setRemaining(duration);
    }
  }, [startCountdown, duration]);

  useInterval(
    () => {
      setRemaining((prevTime) => prevTime - 1);
    },
    isCounting && remaining > 0 ? MS_SECOND : null,
  );

  useEffect(() => {
    if (isCounting && remaining <= 0) {
      setIsCounting(false);
      setIsExpired(true);

      onExpire?.();
    }
  }, [isCounting, remaining, onExpire]);

  /* ---- render buttons ---- */
  if (isCounting) {
    const formattedTime = formatTime(remaining);

    return (
      <Button {...props} disabled={true}>
        {countingLabel(formattedTime)}
      </Button>
    );
  }

  if (isExpired) {
    return (
      <Button {...props} variant={finishedVariant || props.variant}>
        {finishedLabel}
      </Button>
    );
  }

  return <Button {...props}>{initialLabel}</Button>;
};

export { CountdownButton };
